import { keyframes } from '@emotion/react'
import { Radii, Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable, keyboardFocusRing } from '@mangovoice/design-system'

export enum QuickActionButtonVariants {
  Default_300 = 'Default_300',
  Default_200 = 'Default_200',
  Default_150 = 'Default_150',
  Default_100 = 'Default_100',
  Default_000 = 'Default_000',
  Destructive_300 = 'Destructive_300',
  Destructive_200 = 'Destructive_200',
  Destructive_100 = 'Destructive_100',
  Active_200 = 'Active_200',
  Greyed_Out = 'Greyed_Out',
  Pulsate_Blue = 'Pulsate_Blue',
}

const quickActionButtonStyleParts = ['tooltip', 'iconButton', 'icon'] as const

const helpers = createMultiStyleConfigHelpers(quickActionButtonStyleParts)

const pulsateAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`

export const QuickActionButtonStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    tooltip: {
      border: '1px solid transparent',
      borderRadius: '.25rem',
      paddingY: '.25rem',
      paddingX: '.75rem',
    },
    iconButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: Radii.sm,
      border: 'none',
      backgroundColor: 'transparent',
      transition:
        'background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      ...keyboardFocusRing,
    },
    icon: {
      color: 'fg.default',
    },
  },
  variants: {
    [QuickActionButtonVariants.Default_300]: {
      iconButton: {
        backgroundColor: 'brand.solid',
        _hover: {
          backgroundColor: 'brand.fg',
        },
        _active: {
          backgroundColor: 'brand.fg',
        },
        _disabled: {
          backgroundColor: 'hover.emphasized',
          boxShadow: 'none',
          cursor: 'default',
        },
      },
      icon: {
        color: 'brand.contrast',
        _groupHover: {
          color: 'brand.contrast',
        },
        _groupActive: {
          color: 'brand.contrast',
        },
        _groupDisabled: {
          color: 'brand.contrast',
        },
      },
    },
    [QuickActionButtonVariants.Default_200]: {
      iconButton: {
        backgroundColor: 'bg.default',
        _hover: {
          backgroundColor: 'brand.subtle',
        },
        _active: {
          backgroundColor: 'brand.subtle',
        },
        _disabled: {
          backgroundColor: 'bg.subtle',
          cursor: 'default',
        },
      },
      icon: {
        color: 'fg.default',
        _groupHover: {
          color: 'brand.fg',
        },
        _groupActive: {
          color: 'brand.fg',
        },
        _groupDisabled: {
          color: 'gray.600',
        },
      },
    },
    [QuickActionButtonVariants.Default_150]: {
      iconButton: {
        backgroundColor: 'transparent',
        _hover: {
          backgroundColor: 'brand.muted',
        },
        _active: {
          backgroundColor: 'brand.muted',
        },
        _disabled: {
          backgroundColor: 'hover.subtle',
          cursor: 'default',
        },
      },
      icon: {
        color: 'fg.default',
        _groupHover: {
          color: 'brand.fg',
        },
        _groupActive: {
          color: 'brand.fg',
        },
        _groupDisabled: {
          color: 'fg.muted',
        },
      },
    },
    [QuickActionButtonVariants.Default_100]: {
      iconButton: {
        backgroundColor: 'transparent',
        _hover: {
          backgroundColor: 'bg.muted',
        },
        _active: {
          backgroundColor: 'bg.emphasized',
        },
        _disabled: {
          backgroundColor: 'transparent',
          cursor: 'default',
        },
      },
      icon: {
        color: 'fg.default',
        _groupHover: {
          color: 'fg.default',
        },
        _groupActive: {
          color: 'fg.default',
        },
        _groupDisabled: {
          color: 'fg.muted',
        },
      },
    },
    [QuickActionButtonVariants.Default_000]: {
      iconButton: {
        backgroundColor: 'transparent',
        _hover: {
          backgroundColor: 'transparent',
        },
        _active: {
          backgroundColor: 'transparent',
        },
        _disabled: {
          backgroundColor: 'transparent',
          cursor: 'default',
        },
      },
      icon: {
        color: 'fg.default',
        _groupHover: {
          color: 'fg.default',
        },
        _groupActive: {
          color: 'fg.default',
        },
        _groupDisabled: {
          color: 'fg.muted',
        },
      },
    },
    [QuickActionButtonVariants.Destructive_300]: {
      iconButton: {
        backgroundColor: 'red.solid',
        _hover: {
          backgroundColor: 'red.emphasized',
          boxShadow: asChakraVariable({ default: 'shadows.error' }),
        },
        _active: {
          backgroundColor: 'red.solid',
          boxShadow: asChakraVariable({ default: 'shadows.error' }),
        },
        _disabled: {
          backgroundColor: 'hover.emphasized',
          cursor: 'default',
        },
      },
      icon: {
        color: 'red.contrast',
        _groupHover: {
          color: 'red.contrast',
        },
        _groupActive: {
          color: 'red.contrast',
        },
        _groupDisabled: {
          color: 'red.contrast',
        },
      },
    },
    [QuickActionButtonVariants.Destructive_200]: {
      iconButton: {
        backgroundColor: 'red.subtle',
        _hover: {
          backgroundColor: 'red.muted',
        },
        _active: {
          backgroundColor: 'red.muted',
        },
        _disabled: {
          backgroundColor: 'hover.subtle',
          cursor: 'default',
        },
      },
      icon: {
        color: 'red.solid',
        _groupHover: {
          color: 'red.solid',
        },
        _groupActive: {
          color: 'red.solid',
        },
        _groupDisabled: {
          color: 'fg.muted',
        },
      },
    },
    [QuickActionButtonVariants.Destructive_100]: {
      iconButton: {
        backgroundColor: 'transparent',
        _hover: {
          backgroundColor: 'red.subtle',
        },
        _active: {
          backgroundColor: 'red.muted',
        },
        _disabled: {
          backgroundColor: 'transparent',
          cursor: 'default',
        },
      },
      icon: {
        color: 'red.solid',
        _groupHover: {
          color: 'red.solid',
        },
        _groupActive: {
          color: 'red.solid',
        },
        _groupDisabled: {
          color: 'fg.muted',
        },
      },
    },
    [QuickActionButtonVariants.Greyed_Out]: {
      iconButton: {
        backgroundColor: 'transparent',
        _hover: {
          backgroundColor: 'transparent',
        },
        _active: {
          backgroundColor: 'transparent',
        },
        _disabled: {
          backgroundColor: 'transparent',
          cursor: 'default',
        },
      },
      icon: {
        color: 'border.default',
        _disabled: {
          color: 'border.default',
        },
      },
    },
    [QuickActionButtonVariants.Pulsate_Blue]: {
      iconButton: {
        backgroundColor: 'transparent',
        _hover: {
          backgroundColor: 'transparent',
        },
        _active: {
          backgroundColor: 'transparent',
        },
        _disabled: {
          backgroundColor: 'transparent',
          cursor: 'default',
        },
      },
      icon: {
        color: 'brand.solid',
        animation: `${pulsateAnimation} 1.5s infinite`,
        _disabled: {
          color: 'brand.solid',
        },
      },
    },

    [QuickActionButtonVariants.Active_200]: {
      iconButton: {
        backgroundColor: 'bg.inverted',
        _hover: {
          backgroundColor: 'brand.solid',
        },
        _active: {
          backgroundColor: 'brand.emphasized',
        },
        _disabled: {
          backgroundColor: 'hover.subtle',
          cursor: 'default',
        },
      },
      icon: {
        color: 'fg.inverted',
        _groupHover: {
          color: 'brand.contrast',
        },
        _groupActive: {
          color: 'brand.contrast',
        },
        _groupDisabled: {
          color: 'fg.muted',
        },
      },
    },
  },
  sizes: {},
  defaultProps: {
    variant: QuickActionButtonVariants.Default_300,
    size: Size.md,
  },
})

export const getQuickActionButtonStyles = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(QuickActionButtonStyle, props) as MultiStyleType<
    typeof quickActionButtonStyleParts
  >
}
